/**
 * ComparisonSplitView Component
 *
 * @deprecated Use ComparisonView from '@/components/ComparisonView' instead.
 * This component is kept for backwards compatibility but will be removed in a future version.
 *
 * Desktop side-by-side comparison view.
 * Shows field-by-field differences organized by form sections.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { Typography, Switch, Space, Tag, Empty, Alert, Button, Tooltip, message } from 'antd'
import { SwapOutlined, DiffOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons'
import type { FieldMetadata, FormSection, DisplayMetadata } from '@/services/metadataService'
import type { ComparisonConfig } from './types'
import { useFieldComparison } from './useFieldComparison'
import {
  ComparisonFieldSection,
  groupDifferencesBySection,
  getOrderedSections,
} from './ComparisonFieldSection'
import { formatComparisonAsText, copyTextToClipboard } from './utils/copyToClipboard'

const { Text } = Typography

/**
 * Format record display title.
 */
function formatRecordTitle(record: Record<string, unknown> | null): string {
  if (!record) return 'No record selected'
  const name = record.name || record.title || record.label || record.uid
  return String(name ?? 'Unnamed')
}

/**
 * Get lifecycle state color for tag.
 */
function getStateColor(state: string | undefined): string {
  if (!state) return 'default'
  const colorMap: Record<string, string> = {
    draft: 'processing',
    active: 'success',
    expired: 'default',
    pending: 'warning',
  }
  return colorMap[state.toLowerCase()] || 'default'
}

export interface ComparisonSplitViewProps {
  /** Anchor record (left panel - what we're comparing from) */
  anchorRecord: Record<string, unknown> | null
  /** Comparison record (right panel - baseline to compare against) */
  comparisonRecord: Record<string, unknown> | null
  /** Field metadata for formatting */
  fields: Record<string, FieldMetadata>
  /** Form sections for grouping */
  formSections: FormSection[]
  /** Display metadata for entity labels */
  display?: DisplayMetadata
  /** Comparison configuration */
  comparison?: ComparisonConfig | null
  /** Show only fields with differences */
  showDifferencesOnly: boolean
  /** Callback to toggle differences only mode */
  onToggleDifferencesOnly: () => void
  /** Collapsed section keys */
  collapsedSections: Set<string>
  /** Callback to toggle section collapse */
  onToggleSection: (sectionKey: string) => void
}

/**
 * Desktop split view for comparing two records side-by-side.
 *
 * @example
 * ```tsx
 * <ComparisonSplitView
 *   anchorRecord={currentRecord}
 *   comparisonRecord={selectedComparison}
 *   fields={metadata.fields}
 *   formSections={metadata.formSections}
 *   display={metadata.display}
 *   comparison={metadata.comparison}
 *   showDifferencesOnly={preferences.showDifferencesOnly}
 *   onToggleDifferencesOnly={toggleShowDifferencesOnly}
 *   collapsedSections={new Set(preferences.collapsedSections)}
 *   onToggleSection={toggleSectionCollapsed}
 * />
 * ```
 */
export function ComparisonSplitView({
  anchorRecord,
  comparisonRecord,
  fields,
  formSections,
  display: _display,
  comparison,
  showDifferencesOnly,
  onToggleDifferencesOnly,
  collapsedSections,
  onToggleSection,
}: ComparisonSplitViewProps): JSX.Element {
  // Note: _display reserved for future use (e.g., entity name in messages)

  // Copy state
  const [isCopied, setIsCopied] = useState(false)

  // Screen reader announcement state
  const [announcement, setAnnouncement] = useState('')
  const announcementRef = useRef<HTMLDivElement>(null)

  // Calculate field differences
  const {
    differences,
    changedCount,
    unchangedCount,
    hasDifferences,
    keyFieldChanges,
    hasComparison,
  } = useFieldComparison({
    anchorRecord,
    comparisonRecord,
    fields,
    comparison,
    showDifferencesOnly,
  })

  // Announce comparison results to screen readers when they change
  useEffect(() => {
    if (comparisonRecord) {
      const msg = hasDifferences
        ? `Comparison loaded. ${changedCount} field${changedCount !== 1 ? 's' : ''} changed${keyFieldChanges.length > 0 ? `, including ${keyFieldChanges.length} key field${keyFieldChanges.length !== 1 ? 's' : ''}` : ''}.`
        : 'Comparison loaded. No differences found.'
      setAnnouncement(msg)
    }
  }, [comparisonRecord, hasDifferences, changedCount, keyFieldChanges.length])

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const text = formatComparisonAsText(
        anchorRecord,
        comparisonRecord,
        differences,
        fields,
        formSections
      )
      await copyTextToClipboard(text)
      setIsCopied(true)
      message.success('Comparison copied to clipboard')

      // Reset icon after 2 seconds
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      message.error('Failed to copy to clipboard')
    }
  }, [anchorRecord, comparisonRecord, differences, fields, formSections])

  // Key fields set for highlighting
  const keyFields = new Set(comparison?.keyFields ?? [])

  // Group differences by section
  const sectionGroups = groupDifferencesBySection(differences, fields, formSections)
  const orderedSections = getOrderedSections(formSections, sectionGroups)

  // Get lifecycle state from records
  const anchorState = anchorRecord?.lifecycleState as string | undefined
  const comparisonState = comparisonRecord?.lifecycleState as string | undefined

  return (
    <div className="comparison-split-view" role="region" aria-label="Record comparison">
      {/* Screen reader announcements */}
      <div
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {announcement}
      </div>

      {/* Header with record summary */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 16,
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: 8,
        }}
      >
        {/* Comparison Record (current/baseline - left) */}
        <div style={{ flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
            Current
          </Text>
          <div style={{ marginTop: 4 }}>
            {hasComparison ? (
              <Space>
                <Text strong>{formatRecordTitle(comparisonRecord)}</Text>
                {comparisonState && (
                  <Tag color={getStateColor(comparisonState)}>
                    {comparisonState}
                  </Tag>
                )}
                {comparisonRecord?.lifecycleVersion != null && (
                  <Tag color="blue" style={{ fontSize: 11 }}>
                    v{String(comparisonRecord.lifecycleVersion)}
                  </Tag>
                )}
              </Space>
            ) : (
              <Text type="secondary" italic>
                Select a record to compare
              </Text>
            )}
          </div>
        </div>

        {/* Arrow indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            color: hasComparison ? 'var(--rp-blue-primary)' : 'var(--rp-border-subtle)',
          }}
        >
          <SwapOutlined style={{ fontSize: 18 }} />
        </div>

        {/* Anchor Record (revised - right) */}
        <div style={{ flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
            Revised
          </Text>
          <div style={{ marginTop: 4 }}>
            <Space>
              <Text strong>{formatRecordTitle(anchorRecord)}</Text>
              {anchorState && (
                <Tag color={getStateColor(anchorState)}>
                  {anchorState}
                </Tag>
              )}
              {anchorRecord?.lifecycleVersion != null && (
                <Tag color="blue" style={{ fontSize: 11 }}>
                  v{String(anchorRecord.lifecycleVersion)}
                </Tag>
              )}
            </Space>
          </div>
        </div>
      </div>

      {/* Summary and toggle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Space>
          <DiffOutlined style={{ color: hasComparison && hasDifferences ? 'var(--rp-temporal-orange)' : hasComparison ? 'var(--rp-success)' : 'var(--rp-gray-500)' }} />
          {!hasComparison ? (
            <Text type="secondary">
              {differences.length} field{differences.length !== 1 ? 's' : ''} to compare
            </Text>
          ) : hasDifferences ? (
            <Text>
              <Text strong style={{ color: 'var(--rp-temporal-orange)' }}>{changedCount}</Text>
              {' '}field{changedCount !== 1 ? 's' : ''} changed
              {unchangedCount > 0 && !showDifferencesOnly && (
                <Text type="secondary">
                  , {unchangedCount} unchanged
                </Text>
              )}
            </Text>
          ) : (
            <Text type="success">
              No differences found
            </Text>
          )}
        </Space>

        <Space size="middle">
          <Space>
            <Text type="secondary">Show differences only</Text>
            <Switch
              size="small"
              checked={showDifferencesOnly}
              onChange={onToggleDifferencesOnly}
              disabled={!hasComparison}
            />
          </Space>
          <Tooltip title="Copy comparison to clipboard">
            <Button
              type="text"
              size="small"
              icon={isCopied ? <CheckOutlined style={{ color: 'var(--rp-success)' }} /> : <CopyOutlined />}
              onClick={handleCopy}
              aria-label="Copy comparison to clipboard"
              disabled={!hasComparison}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Key field changes alert */}
      {keyFieldChanges.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              <Text strong>{keyFieldChanges.length}</Text> key field
              {keyFieldChanges.length !== 1 ? 's' : ''} changed:{' '}
              {keyFieldChanges.map((kf, idx) => (
                <span key={kf.fieldKey}>
                  <Text strong>{kf.fieldKey}</Text>
                  {idx < keyFieldChanges.length - 1 ? ', ' : ''}
                </span>
              ))}
            </span>
          }
        />
      )}

      {/* Field sections */}
      {orderedSections.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Empty
            description={
              showDifferencesOnly
                ? 'No differences found'
                : 'No comparable fields available'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="comparison-sections">
          {orderedSections.map(section => {
            const sectionDiffs = sectionGroups.get(section.name) || []

            return (
              <ComparisonFieldSection
                key={section.name}
                section={section}
                differences={sectionDiffs}
                fields={fields}
                keyFields={keyFields}
                expanded={!collapsedSections.has(section.name)}
                onExpandChange={(expanded) => {
                  if (!expanded !== collapsedSections.has(section.name)) {
                    onToggleSection(section.name)
                  }
                }}
                hasComparison={hasComparison}
              />
            )
          })}
        </div>
      )}

      {/* Column Legend */}
      <div
        style={{
          marginTop: 16,
          padding: '8px 12px',
          backgroundColor: 'var(--rp-surface-muted)',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        <Text type="secondary">
          <strong>Current</strong> (left) shows the comparison record.{' '}
          <strong>Revised</strong> (right) shows the record being edited.{' '}
          {hasComparison ? 'Yellow highlighting indicates differences.' : 'Select a comparison record to see differences.'}
        </Text>
      </div>
    </div>
  )
}

export default ComparisonSplitView
